import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { api } from "../../lib/api";
import {
  notifyDrafts,
  registerProposalDraft,
} from "../../lib/proposalDraftRegistry";
import type { ProposalInspectionPhoto } from "../../types/proposals";

type Selected = { photo: number; description: string };
type Selection = {
  available: ProposalInspectionPhoto[];
  selected: Selected[];
  mode: string;
};
export default function ProposalPhotoSelection({ id }: { id: number }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["proposal-photo-selection", id],
    queryFn: async () =>
      (await api.get<Selection>(`/api/proposals/${id}/photo-selection/`)).data,
  });
  const [draft, setDraft] = useState<Selected[] | null>(null);
  const dirty = useRef<Selected[] | null>(null);
  const save = useMutation({
    mutationKey: ["proposal-editor", String(id)],
    mutationFn: async (photos: Selected[]) =>
      (
        await api.post<Selection>(`/api/proposals/${id}/photo-selection/`, {
          photos,
        })
      ).data,
    onSuccess: (data, submitted) => {
      client.setQueryData(["proposal-photo-selection", id], data);
      if (JSON.stringify(dirty.current) === JSON.stringify(submitted)) {
        dirty.current = null;
        setDraft(null);
        notifyDrafts();
      }
      void client.invalidateQueries({ queryKey: ["proposal", String(id)] });
      void client.invalidateQueries({
        queryKey: ["proposal-editor-history", String(id)],
      });
    },
  });
  const flush = useRef(() => {});
  flush.current = () => {
    if (dirty.current && !save.isPending) save.mutate(dirty.current);
  };
  useEffect(
    () =>
      registerProposalDraft({
        flush: () => flush.current(),
        dirty: () => dirty.current !== null,
      }),
    [id],
  );
  useEffect(() => {
    if (!draft) void query.refetch();
  }, [id]); // Reset data when switching proposals.
  const rows = draft || query.data?.selected || [];
  const change = (rows: Selected[]) => {
    dirty.current = rows;
    setDraft(rows);
    notifyDrafts();
  };
  const move = (index: number, offset: number) => {
    const next = [...rows];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    change(next);
  };
  if (query.isPending)
    return <Typography>Loading inspection photos…</Typography>;
  if (!query.data)
    return (
      <Alert
        color="warning"
        endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}
      >
        Photos could not be loaded.
      </Alert>
    );
  return (
    <Card>
      <Typography level="title-lg">
        Choose & arrange inspection photos
      </Typography>
      <Typography level="body-sm">
        Saving this selection switches the proposal to selected photos. Choose
        “All opportunity photos” above to include everything again.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
          gap: 1,
        }}
      >
        {query.data.available.map((photo) => (
          <Card key={photo.photo} size="sm">
            <img
              src={photo.photo_thumbnail || photo.photo_url || undefined}
              alt={photo.description || "Inspection photo"}
              style={{ width: "100%", height: 95, objectFit: "contain" }}
            />
            <Checkbox
              label={`Photo ${photo.photo}`}
              checked={rows.some((row) => row.photo === photo.photo)}
              disabled={save.isPending}
              onChange={(event) =>
                change(
                  event.target.checked
                    ? [
                        ...rows,
                        {
                          photo: photo.photo,
                          description: photo.description || "",
                        },
                      ]
                    : rows.filter((row) => row.photo !== photo.photo),
                )
              }
            />
          </Card>
        ))}
      </Box>
      {!query.data.available.length && (
        <Typography>No opportunity photos are available.</Typography>
      )}
      {rows.map((row, i) => (
        <Stack key={row.photo} spacing={1}>
          <Stack direction="row" spacing={1}>
            <Typography level="title-sm">
              {i + 1}. Photo {row.photo}
            </Typography>
            <Button
              size="sm"
              variant="plain"
              disabled={i === 0 || save.isPending}
              onClick={() => move(i, -1)}
              aria-label={`Move photo ${row.photo} up`}
            >
              ↑
            </Button>
            <Button
              size="sm"
              variant="plain"
              disabled={i === rows.length - 1 || save.isPending}
              onClick={() => move(i, 1)}
              aria-label={`Move photo ${row.photo} down`}
            >
              ↓
            </Button>
          </Stack>
          <Textarea
            value={row.description}
            disabled={save.isPending}
            placeholder="Caption"
            slotProps={{
              textarea: {
                "aria-label": `Caption for photo ${row.photo}`,
                maxLength: 3000,
              },
            }}
            onChange={(event) =>
              change(
                rows.map((item) =>
                  item.photo === row.photo
                    ? { ...item, description: event.target.value }
                    : item,
                ),
              )
            }
          />
        </Stack>
      ))}
      {save.isError && (
        <Alert color="danger">
          Photos could not be saved. Your selection is retained. Retry before
          sharing.
        </Alert>
      )}
      <Button
        onClick={() => flush.current()}
        loading={save.isPending}
        disabled={!draft}
      >
        Save photo selection
      </Button>
    </Card>
  );
}
